import { useCallback, useEffect, useRef, useState } from "react";
import {
  API_ROUTES,
  applyPasjansAction,
  clonePasjansState,
  newGameFromSeed,
  randomPasjansSeed,
  type PasjansAction,
  type PasjansDest,
  type PasjansScore,
  type PasjansSource,
  type PasjansState,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { formatDate } from "../lib/format.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSession } from "../session/SessionProvider.js";
import { PasjansBoard } from "../games/pasjans/PasjansBoard.js";
import { ADMIN_LIST, AdminDeleteButton, useAdminListTools } from "../components/admin/AdminChrome.js";
import "../styles/pasjans.css";

const HISTORY_LIMIT = 80;

function deal(): { seed: number; state: PasjansState } {
  const seed = randomPasjansSeed();
  return { seed, state: newGameFromSeed(seed) };
}

function initialDeal(): { seed: number; state: PasjansState } {
  const game = deal();
  if (!import.meta.env.DEV) return game;
  if (new URLSearchParams(window.location.search).get("win") !== "1") return game;
  return { ...game, state: { ...game.state, won: true, score: 420, moves: 88 } };
}

function formatClock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

export function PasjansPage() {
  const { user } = useSession();
  const [dealSeed] = useState(initialDeal);
  const [seed, setSeed] = useState(dealSeed.seed);
  const [state, setState] = useState<PasjansState>(dealSeed.state);
  const [actions, setActions] = useState<PasjansAction[]>([]);
  const [history, setHistory] = useState<PasjansState[]>([]);
  const [paused, setPaused] = useState(false);
  const [started, setStarted] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [saved, setSaved] = useState(false);
  const scores = useLeaderboard();
  const admin = useAdminListTools({
    ...ADMIN_LIST.pasjans,
    onReload: scores.reload,
  });
  const { busy, submit } = useFormSubmit();
  const stateRef = useRef(state);
  stateRef.current = state;

  const ticking = started && !paused && !state.won;

  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setElapsedSec((value) => value + 1), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);

  const restart = useCallback(() => {
    const next = deal();
    setSeed(next.seed);
    setState(next.state);
    setActions([]);
    setHistory([]);
    setPaused(false);
    setStarted(false);
    setElapsedSec(0);
    setSaved(false);
  }, []);

  const play = useCallback((action: PasjansAction) => {
    const next = applyPasjansAction(stateRef.current, action);
    if (!next || next === stateRef.current) return false;
    if (paused || stateRef.current.won) return false;
    const snapshot = clonePasjansState(stateRef.current);
    setHistory((stack) => {
      const stacked = [...stack, snapshot];
      return stacked.length > HISTORY_LIMIT ? stacked.slice(-HISTORY_LIMIT) : stacked;
    });
    setActions((log) => [...log, action]);
    if (action.type !== "draw") setStarted(true);
    setState(next);
    return true;
  }, [paused]);

  const undo = useCallback(() => {
    if (paused) return;
    setHistory((stack) => {
      if (stack.length === 0) return stack;
      const previous = stack[stack.length - 1];
      if (previous) setState(previous);
      return stack.slice(0, -1);
    });
    setActions((log) => log.slice(0, -1));
  }, [paused]);

  const onMove = useCallback(
    (source: PasjansSource, dest: PasjansDest) => play({ type: "move", source, dest }),
    [play],
  );

  const onAutoFoundation = useCallback(
    (source: PasjansSource) => play({ type: "auto", source }),
    [play],
  );

  const onStock = useCallback(() => {
    play({ type: "stock" });
  }, [play]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        undo();
        return;
      }
      if (event.key === "Escape" && !stateRef.current.won) {
        event.preventDefault();
        setPaused((value) => !value);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  async function saveScore() {
    const ok = await submit(async () => {
      await apiRequest<PasjansScore>(API_ROUTES.pasjans.scores, {
        method: "POST",
        body: { score: state.score, moves: state.moves, seed, actions },
      });
      await scores.reload();
    }, "Wynik zapisany");
    if (ok) setSaved(true);
  }

  return (
    <div className="pasjans">
      <section className="pasjans__play">
        <header className="pasjans__header">
          <h2 className="pasjans__title">
            <i className="z-icon" aria-hidden="true">
              🃏
            </i>
            Pasjans Klondike
          </h2>
          <div className="pasjans__controls">
            <Button variant="primary" onClick={restart}>
              Nowa gra
            </Button>
            <Button onClick={() => play({ type: "draw" })}>
              {state.drawCount === 1 ? "Tryb: 1 karta" : "Tryb: 3 karty"}
            </Button>
            <Button disabled={history.length === 0 || paused} onClick={undo}>
              Cofnij
            </Button>
            <Button disabled={state.won} onClick={() => setPaused((value) => !value)}>
              {paused ? "Wznów" : "Pauza"}
            </Button>
            <div className="pasjans__stats" aria-live="polite">
              <span>
                Wynik: <strong>{state.score}</strong>
              </span>
              <span>
                Ruchy: <strong>{state.moves}</strong>
              </span>
              <span>
                Czas: <strong>{formatClock(elapsedSec)}</strong>
              </span>
            </div>
          </div>
        </header>

        <div className={`pasjans__table${paused ? " pasjans__table--paused" : ""}`}>
          <PasjansBoard
            state={state}
            onStock={onStock}
            onMove={onMove}
            onAutoFoundation={onAutoFoundation}
          />
          {paused ? (
            <div className="pas-pause" role="dialog" aria-modal="true" aria-labelledby="pas-pause-title">
              <div className="pas-pause__panel">
                <h2 id="pas-pause-title">Pauza</h2>
                <p>Karty są schowane. Czas stoi.</p>
                <Button variant="primary" onClick={() => setPaused(false)}>
                  Wznów
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <aside className="pasjans__scores">
        <Card variant="panel" title="Najlepsze wyniki" icon="⭐">
          {admin.isAdmin ? (
            <div className="admin-onpage__toolbar">
              <Button size="sm" variant="danger" disabled={admin.busy} onClick={() => void admin.wipe()}>
                Wyczyść tablicę
              </Button>
            </div>
          ) : null}
          {scores.error ? <p className="hub-error">{scores.error}</p> : null}
          {scores.loading ? (
            <p className="hub-hint">Wczytywanie wyników...</p>
          ) : scores.items.length === 0 ? (
            <p className="hub-hint">Brak wyników</p>
          ) : (
            <ol className="pas-leaderboard">
              {scores.items.map((entry, index) => (
                <li
                  key={entry.id}
                  className={`pas-leaderboard__row${admin.isAdmin ? " pas-leaderboard__row--admin" : ""}`}
                >
                  <span className="pas-leaderboard__rank">{index + 1}.</span>
                  <span className="pas-leaderboard__name">{entry.username}</span>
                  <span className="pas-leaderboard__score">{entry.score}</span>
                  <span className="pas-leaderboard__moves">{entry.moves}</span>
                  <span className="pas-leaderboard__date">{formatDate(entry.createdAt)}</span>
                  {admin.isAdmin ? (
                    <AdminDeleteButton
                      confirm="Usunąć ten wynik?"
                      disabled={admin.busy}
                      onConfirm={() => admin.remove(entry.id)}
                    />
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </aside>

      {state.won ? (
        <div className="pas-overlay" role="dialog" aria-modal="true" aria-labelledby="pas-win-title">
          <div className="pas-overlay__panel">
            <h2 id="pas-win-title">Wygrałeś!</h2>
            <p>Wynik: {state.score}</p>
            <p>Ruchy: {state.moves}</p>
            <p>Czas: {formatClock(elapsedSec)}</p>
            {user ? (
              saved ? (
                <p className="hub-hint">Wynik jest już na tablicy.</p>
              ) : (
                <p className="hub-hint">Zapisze się jako {user.username}.</p>
              )
            ) : (
              <p className="hub-hint">Zaloguj się u góry, żeby zapisać wynik.</p>
            )}
            <div className="pas-overlay__actions">
              {user && !saved ? (
                <Button variant="primary" disabled={busy} onClick={() => void saveScore()}>
                  {busy ? "Zapisywanie..." : "Zapisz wynik"}
                </Button>
              ) : null}
              <Button variant={user && !saved ? "ghost" : "primary"} onClick={restart}>
                Zagraj ponownie
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function useLeaderboard() {
  const [items, setItems] = useState<PasjansScore[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const next = await apiRequest<PasjansScore[]>(API_ROUTES.pasjans.scores);
      setItems(next);
      setError(null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { items, error, loading, reload };
}
